import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, addDays } from "date-fns";
import { Plus, FileText, CalendarIcon, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { FormDrawer } from "@/components/ui/form-drawer";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { DateCell } from "@/components/DateCell";

interface Quote {
  id: string;
  quoteNumber: string;
  accountId: string;
  status: string;
  total: string | number;
  marginPercent: string | number;
  createdAt: string;
  account?: { name: string };
}

interface Account {
  id: string;
  name: string;
}

interface AccountsResponse {
  accounts: Account[];
  total: number;
}

interface QuotesResponse {
  quotes: Quote[];
  total: number;
}

const quoteFormSchema = z.object({
  accountId: z.string().min(1, "Please select an account"),
  validUntil: z.date(),
  paymentTerms: z.string().optional(),
  notes: z.string().optional(),
});

type QuoteFormData = z.infer<typeof quoteFormSchema>;

const FALLBACK_PAYMENT_TERMS = [
  { value: "NET15", label: "Net 15" },
  { value: "NET30", label: "Net 30" },
  { value: "NET45", label: "Net 45" },
  { value: "NET60", label: "Net 60" },
  { value: "DUE_ON_RECEIPT", label: "Due on Receipt" },
];

export default function QuotesPage() {
  const [, setLocation] = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [newQuoteDrawerOpen, setNewQuoteDrawerOpen] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const { toast } = useToast();
  const { sessionId } = useAuth();

  const { data: quotesData, isLoading: quotesLoading } = useQuery({
    queryKey: ['quotes', sessionId, currentPage, pageSize, includeArchived],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(pageSize),
      });
      if (includeArchived) {
        params.append('includeArchived', 'true');
      }
      return api.get<QuotesResponse>(`/quotes?${params.toString()}`, sessionId);
    },
    enabled: !!sessionId,
  });

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts', sessionId],
    queryFn: async () => {
      return api.get<AccountsResponse>('/accounts?limit=500', sessionId);
    },
    enabled: !!sessionId && newQuoteDrawerOpen,
  });
  const accounts = accountsData?.accounts || [];

  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', sessionId],
    queryFn: async () => {
      return api.get<{ leads: Account[] }>('/leads?limit=500', sessionId);
    },
    enabled: !!sessionId && newQuoteDrawerOpen,
  });
  const leads = leadsData?.leads || [];
  const quotes = quotesData?.quotes || [];
  const totalCount = quotesData?.total || 0;

  const form = useForm<QuoteFormData>({
    resolver: zodResolver(quoteFormSchema),
    defaultValues: {
      accountId: "",
      validUntil: addDays(new Date(), 30),
      paymentTerms: "NET30",
      notes: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: QuoteFormData) => {
      return api.post<{ id: string }>('/quotes', {
        ...data,
        lineItems: [],
      }, sessionId);
    },
    onSuccess: (quote: { id: string }) => {
      toast({
        title: "Quote created",
        description: "Redirecting to add line items...",
      });
      setNewQuoteDrawerOpen(false);
      form.reset();
      setLocation(`/quotes/${quote.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create quote",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: QuoteFormData) => {
    createMutation.mutate(data);
  };

  const columns: DataTableColumn<Quote>[] = [
    {
      key: "createdAt",
      header: "Entry",
      sortable: true,
      cell: (row) => (
        <DateCell value={row.createdAt} className="text-muted-foreground" />
      ),
    },
    {
      key: "quoteNumber",
      header: "Quote #",
      sortable: true,
      cell: (row) => (
        <div className="font-medium">{row.quoteNumber}</div>
      ),
    },
    {
      key: "account",
      header: "Account",
      cell: (row) => (
        <span>{row.account?.name || "-"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge status={row.status} type="quote" />,
    },
    {
      key: "total",
      header: "Total",
      className: "text-right",
      cell: (row) => (
        <span className="font-medium">${Number(row.total || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "marginPercent",
      header: "Margin",
      className: "text-right",
      cell: (row) => (
        <span className="text-muted-foreground">{Number(row.marginPercent || 0).toFixed(1)}%</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Quotes"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant={includeArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setIncludeArchived((v) => !v)}
              data-testid="button-toggle-archived"
            >
              <Archive className="h-3.5 w-3.5 mr-1.5" />
              {includeArchived ? "Hide archived" : "Show archived"}
            </Button>
            <Button size="sm" onClick={() => setNewQuoteDrawerOpen(true)} data-testid="button-new-quote">
              <Plus className="h-4 w-4 mr-1" />
              New Sales Quote
            </Button>
          </div>
        }
      />

      <DataTable
        data={quotes}
        columns={columns}
        isLoading={quotesLoading}
        onRowClick={(row) => setLocation(`/quotes/${row.id}`)}
        emptyMessage="No sales quotes found"
        emptyIcon={<FileText className="h-12 w-12 text-muted-foreground/50" />}
        pageSize={pageSize}
        totalCount={totalCount}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />

      <FormDrawer
        open={newQuoteDrawerOpen}
        onOpenChange={setNewQuoteDrawerOpen}
        title="New Sales Quote"
        description="Create a new quote. You can add line items after creation."
        formId="create-quote"
        isPending={createMutation.isPending}
        submitLabel="Create Sales Quote"
        size="md"
        data-testid="drawer-new-quote"
      >
        <Form {...form}>
          <form
            id="create-quote"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account / Lead *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || undefined}
                    disabled={accountsLoading || leadsLoading}
                  >
                    <FormControl>
                      <SelectTrigger variant="flat" data-testid="select-account">
                        <SelectValue placeholder="Select an account or lead" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accounts.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Accounts</div>
                          {accounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.name}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      {leads.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Leads</div>
                          {leads.map((lead) => (
                            <SelectItem key={lead.id} value={lead.id}>
                              {lead.name}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      {accounts.length === 0 && leads.length === 0 && (
                        <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                          No accounts or leads found
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="validUntil"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Valid Until</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                          data-testid="button-valid-until"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? format(field.value, "PPP") : "Pick a date"}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="paymentTerms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Terms</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger variant="flat" data-testid="select-payment-terms">
                        <SelectValue placeholder="Select terms" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {FALLBACK_PAYMENT_TERMS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      variant="flat"
                      placeholder="Add any notes about this quote..."
                      className="min-h-[100px]"
                      data-testid="textarea-notes"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
